/**
 * Export GST Invoices · Feb 2026
 * Exports counterpart of GstInvoices.jsx. Handles Tax Invoices raised
 * against an Export Sales Order (ESO), Export Packing List (EPL) or
 * Export Commercial Invoice (ECI). Dual-currency (foreign + INR),
 * LUT vs IGST toggle, GSTR-1 Table 6A capture, export declaration.
 */
import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  FileTextIcon, PlusIcon, PrinterIcon, PencilLineIcon, TrashIcon, EyeIcon,
  CheckCircleIcon, WarningIcon, ProhibitIcon, GlobeIcon, XIcon,
} from "@phosphor-icons/react";
import { getUser } from "@/lib/secureStorage";
import { canSubmitDoc } from "@/lib/submitPerms";
import { useAuth } from "@/lib/auth";
import ApprovalControls, { ApprovalBadge } from "@/components/ApprovalControls";
import DocumentInfo from "@/components/DocumentInfo";
import { DocDownloadButtons, DocDownloadIcons, useDocStyles } from "@/components/DocDownloadActions";
import { RowActionsMenu } from "@/components/RowActionsMenu";

const STATUS_STYLE = {
  Draft:     "border-zinc-500/40 text-zinc-300",
  Issued:    "border-emerald-500/40 text-emerald-300 bg-emerald-500/5",
  Amended:   "border-amber-500/40 text-amber-300 bg-amber-500/5",
  Cancelled: "border-red-500/40 text-red-300 bg-red-500/5",
};
const CCY_OPTIONS = ["USD", "EUR", "GBP", "AED", "AUD", "CAD", "JPY", "CNY", "SGD", "CHF"];

// A1-D03 · P2.5-U01 · Feb 2026 — canonical GST Treatment options
// mirror the ECI entry (`/app/frontend/src/pages/Exports.jsx` GST_TYPES).
// EGSTI inherits the ECI's `gst_type` verbatim as `gst_treatment` and
// cannot be independently edited for an ECI-linked EGSTI.
const EGSTI_GST_TYPES = [
  { v: "LUT",     label: "LUT (0% IGST)" },
  { v: "IGST_5",  label: "IGST 5%" },
  { v: "IGST_12", label: "IGST 12%" },
  { v: "IGST_18", label: "IGST 18%" },
];
const _egstiGstLabel = (v) => {
  const hit = EGSTI_GST_TYPES.find(x => x.v === String(v || "").toUpperCase());
  return hit ? hit.label : "—";
};

const fmt = (v, d = 2) => {
  const n = Number(v);
  if (!isFinite(n)) return "0.00";
  return n.toLocaleString("en-US", { minimumFractionDigits: d, maximumFractionDigits: d });
};

export default function ExportGstInvoices() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  // DP-EGSTI-PENDING-EDIT-LOCK-01 · Mar 2026 — force-read-only flag set
  // by the row-level View action. Distinct from the editor's internal
  // readOnly derivation (which also blocks Cancelled / Shipping-Bill
  // stamped docs) so View works even for a genuinely-editable Draft.
  const [viewOnly, setViewOnly] = useState(false);
  const user = getUser();
  const { hasPerm } = useAuth();
  // Aug 31, 2026 · Zero-Trust — DB-backed. Same contract as GST Invoices.
  const canEdit = user?.role === "Administrator"
    || hasPerm?.("export_gst_invoices:create") || hasPerm?.("export_gst_invoices:edit");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/export-gst-invoices/");
      setRows(data || []);
    } catch {
      toast.error("Failed to load Export GST invoices");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return rows.filter(r => {
      if (statusFilter && r.status !== statusFilter) return false;
      if (!needle) return true;
      const hay = [
        r.invoice_no, r.buyer?.name, r.buyer?.country, (r.source || {}).no,
        r.buyer_po_no, r.shipping_bill_no,
      ].filter(Boolean).join(" ").toLowerCase();
      return hay.includes(needle);
    });
  }, [rows, q, statusFilter]);

  const openNew = () => { setEditing(null); setViewOnly(false); setEditorOpen(true); };
  const openEdit = async (inv) => {
    try {
      const { data } = await api.get(`/export-gst-invoices/${inv.id}`);
      setEditing(data);
      setViewOnly(false);
      setEditorOpen(true);
    } catch { toast.error("Failed to open invoice"); }
  };
  // DP-EGSTI-PENDING-EDIT-LOCK-01 · Mar 2026 — read-only entry point.
  // Always available with `export_gst_invoices:view`, regardless of the
  // document's approval state (Pending / Rejected / Authorised /
  // Issued / Cancelled) — parity with the ratified Action Menu table.
  const openView = async (inv) => {
    try {
      const { data } = await api.get(`/export-gst-invoices/${inv.id}`);
      setEditing(data);
      setViewOnly(true);
      setEditorOpen(true);
    } catch { toast.error("Failed to open invoice"); }
  };

  const openHtmlInNewTab = (html) => {
    const blob = new Blob([html], { type: "text/html;charset=utf-8" });
    const url  = URL.createObjectURL(blob);
    const w = window.open(url, "_blank");
    if (!w) { toast.error("Popup blocked — allow popups for this site"); URL.revokeObjectURL(url); return; }
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  };
  const printHtml = async (inv) => {
    try {
      const res = await api.get(`/export-gst-invoices/${inv.id}/print.html`, { responseType: "text" });
      openHtmlInNewTab(res.data);
    } catch { toast.error("Failed to open print view"); }
  };

  const cancel = async (inv) => {
    const reason = window.prompt(`Cancel ${inv.invoice_no}?\n\nEnter reason (min 4 chars, kept in audit log):`);
    if (!reason || reason.trim().length < 4) { if (reason !== null) toast.error("Reason must be ≥ 4 chars"); return; }
    try {
      await api.post(`/export-gst-invoices/${inv.id}/cancel`, { reason: reason.trim() });
      toast.success(`${inv.invoice_no} · cancelled`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Cancel failed"); }
  };

  // DP-EGSTI-ISSUE-ACTION-01 · Jun 2026 — explicit row-level Issue.
  // Authorise ≠ Issue. Reuses the canonical /issue endpoint; the button
  // that calls this is rendered only for an Authorised Draft (see the
  // Action Menu below). Issue moves Document Status Draft → Issued while
  // Approval Status remains Authorized (two independent axes).
  const issueRow = async (inv) => {
    if (!window.confirm(`Issue ${inv.invoice_no || "this Authorised Draft"}?\n\nAuthorise ≠ Issue — this is the explicit Issue step.\nDocument Status becomes Issued; Approval Status stays Authorized.`)) return;
    try {
      const { data } = await api.post(`/export-gst-invoices/${inv.id}/issue`);
      toast.success(`${data.invoice_no || inv.invoice_no || "Invoice"} · issued`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Issue failed"); }
  };

  const removeDraft = async (inv) => {
    const isDraft = inv.status === "Draft";
    const label = inv.invoice_no || `draft ${inv.id.slice(0, 8)}`;
    const warn = isDraft
      ? `Delete draft ${label}? This cannot be undone.`
      : `⚠ DELETE ${inv.status} invoice ${label}?\n\nThis is a numbered tax invoice — deleting it breaks the FY sequence.\nPrefer Cancel unless you're sure.\n\nType YES to confirm:`;
    if (isDraft) { if (!window.confirm(warn)) return; }
    else { const resp = window.prompt(warn, ""); if (!resp || resp.trim().toUpperCase() !== "YES") return; }
    try {
      await api.delete(`/export-gst-invoices/${inv.id}`);
      toast.success(`${label} · deleted`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };

  const [sbInv, setSbInv] = useState(null);
  const [sbForm, setSbForm] = useState({ shipping_bill_no: "", shipping_bill_date: "", port_code: "" });
  const openStampSb = (inv) => {
    setSbInv(inv);
    setSbForm({
      shipping_bill_no: inv.shipping_bill_no || "",
      shipping_bill_date: inv.shipping_bill_date || new Date().toISOString().slice(0, 10),
      port_code: inv.port_code || "",
    });
  };
  const submitStampSb = async () => {
    if (!sbForm.shipping_bill_no.trim() || !sbForm.port_code.trim()) {
      toast.error("Shipping Bill No + Port Code required"); return;
    }
    try {
      await api.post(`/export-gst-invoices/${sbInv.id}/stamp-shipping-bill`, sbForm);
      toast.success(`Shipping Bill stamped · ${sbForm.shipping_bill_no}`);
      setSbInv(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Stamp failed"); }
  };

  return (
    <div className="p-6 space-y-6" data-testid="export-gst-invoices-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-3xl font-bold flex items-center gap-3">
            <GlobeIcon size={26} weight="bold" className="text-[var(--accent)]" />
            Export GST Invoices
          </h1>
          <p className="text-sm text-zinc-400 mt-1">Tax invoices for export shipments — LUT / IGST toggle, dual-currency, GSTR-1 Table 6A capture.</p>
        </div>
        {canEdit && (
          <button
            data-testid="exp-gst-new-invoice"
            onClick={openNew}
            className="px-4 py-2 rounded-sm bg-[var(--accent)] hover:bg-blue-600 text-white text-sm font-semibold flex items-center gap-2"
          >
            <PlusIcon size={14} weight="bold" /> New Export Invoice
          </button>
        )}
      </div>

      <div className="flex items-center gap-3">
        <input
          data-testid="exp-gst-search"
          value={q}
          onChange={e => setQ(e.target.value)}
          placeholder="Search by invoice no / buyer / country / ESO / shipping bill…"
          className="flex-1 h-10 px-3 bg-[var(--bg-1)] border border-[var(--border)] rounded-sm text-sm focus:outline-none focus:border-[var(--accent)]"
        />
        <select
          data-testid="exp-gst-status-filter"
          value={statusFilter}
          onChange={e => setStatusFilter(e.target.value)}
          className="h-10 px-3 bg-[var(--bg-1)] border border-[var(--border)] rounded-sm text-sm"
        >
          <option value="">All statuses</option>
          <option>Draft</option><option>Issued</option><option>Amended</option><option>Cancelled</option>
        </select>
      </div>

      <div className="tactical-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[var(--bg-2)]/40 text-[10px] uppercase tracking-widest text-zinc-500">
            <tr>
              <th className="px-3 py-2 text-left">Invoice No</th>
              <th className="px-3 py-2 text-left">Date</th>
              <th className="px-3 py-2 text-left">Buyer</th>
              <th className="px-3 py-2 text-left">Source</th>
              <th className="px-3 py-2 text-left">GST</th>
              <th className="px-3 py-2 text-right">Grand Total (FC)</th>
              <th className="px-3 py-2 text-right">INR Equiv.</th>
              <th className="px-3 py-2 text-left">Doc Status</th>
              <th className="px-3 py-2 text-left">Approval</th>
              <th className="px-3 py-2 text-right w-56">Actions</th>
            </tr>
          </thead>
          <tbody data-testid="exp-gst-rows">
            {loading ? (
              <tr><td colSpan={10} className="px-3 py-8 text-center text-zinc-500">Loading…</td></tr>
            ) : filtered.length === 0 ? (
              <tr><td colSpan={10} className="px-3 py-8 text-center text-zinc-500" data-testid="exp-gst-empty">No export invoices — click New Export Invoice to create one.</td></tr>
            ) : filtered.map(r => {
              const ccy = (r.totals?.currency || r.currency || "USD").toUpperCase();
              return (
                <tr key={r.id} className="border-t border-[var(--border)] hover:bg-[var(--bg-2)]/30" data-testid={`exp-gst-row-${r.invoice_no || r.id.slice(0, 8)}`}>
                  <td className="px-3 py-2 font-mono text-[var(--accent)]">{r.invoice_no || <span className="text-zinc-500">— draft —</span>}</td>
                  <td className="px-3 py-2 text-zinc-300">{r.invoice_date || "—"}</td>
                  <td className="px-3 py-2">
                    <div className="font-mono">{r.buyer?.name || "—"}</div>
                    <div className="text-[10px] text-zinc-500">{r.buyer?.country || ""}</div>
                  </td>
                  <td className="px-3 py-2 text-zinc-300 text-xs">{(r.source || {}).type ? `${r.source.type} · ${r.source.no || ""}` : "—"}</td>
                  <td className="px-3 py-2">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-sm border text-[10px] font-mono uppercase tracking-widest ${
                      (r.export_type || "LUT") === "IGST" ? "border-amber-500/40 text-amber-300 bg-amber-500/5" : "border-emerald-500/40 text-emerald-300 bg-emerald-500/5"
                    }`}>
                      {(r.export_type || "LUT") === "IGST" ? "IGST" : "LUT · 0%"}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-right font-mono tabular-nums">{ccy} {fmt(r.totals?.grand_total_fc)}</td>
                  <td className="px-3 py-2 text-right font-mono tabular-nums text-zinc-400">₹ {fmt(r.totals?.grand_total_inr)}</td>
                  <td className="px-3 py-2">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-sm border text-[10px] font-mono uppercase tracking-widest ${STATUS_STYLE[r.status] || ""}`}>
                      {r.status}
                    </span>
                  </td>
                  {/* DP-EGSTI-LIFECYCLE-UI-UNIFORMITY-01 · Jun 2026 —
                      Approval Status column. Reuses the shared
                      <ApprovalBadge> pattern used by the GST Invoice
                      register so the Management Authorisation axis is
                      communicated separately from business Doc Status.
                      An Authorised Draft therefore reads DRAFT · AUTHORIZED
                      (two independent axes, one never overwrites the other). */}
                  <td className="px-3 py-2">
                    <ApprovalBadge
                      status={r.approval_status}
                      reason={r.rejection_reason}
                      testId={`exp-gst-approval-badge-${r.id.slice(0, 8)}`}
                    />
                  </td>
                  <td className="px-3 py-2 text-right">
                    <RowActionsMenu testId={`exp-gst-actions-${r.id.slice(0, 8)}`}>
                    {/* DP-EGSTI-PENDING-EDIT-LOCK-01 · Mar 2026 —
                        Action Menu now evaluates BOTH RBAC and the
                        MA lifecycle state.
                          - View : always available with :view perm
                                   (opens the editor in forced
                                   read-only mode regardless of RBAC
                                   edit permission).
                          - Edit : only when the doc is a Draft AND
                                   approval_status ∈ {"", Not Submitted,
                                   Rejected}. Rendered but DISABLED
                                   with a tooltip while Pending /
                                   Authorised so the operator can see
                                   why the action is unavailable. */}
                    {(() => {
                      const _ap = String(r.approval_status || "").trim();
                      const _pending = _ap === "Pending Approval";
                      const _authorised = _ap === "Authorized" || _ap === "Approved";
                      const _draftEditable = r.status === "Draft" && !_pending && !_authorised;
                      const _editTitle = _pending
                        ? "Editing is locked while this document is Pending Authorisation."
                        : _authorised
                          ? "Editing is locked once the document is Management Authorised."
                          : (canEdit ? "Edit" : "You do not have edit permission");
                      return (
                        <>
                          <button
                            title="View"
                            data-testid={`exp-gst-view-${r.id.slice(0, 8)}`}
                            onClick={() => openView(r)}
                            className="p-1.5 rounded-sm text-zinc-300 hover:bg-[var(--bg-2)]"
                          ><EyeIcon size={13} /></button>
                          <button
                            title={_editTitle}
                            data-testid={`exp-gst-edit-${r.id.slice(0, 8)}`}
                            disabled={!canEdit || !_draftEditable}
                            onClick={() => openEdit(r)}
                            className="p-1.5 rounded-sm text-zinc-300 hover:bg-[var(--bg-2)] disabled:opacity-30 disabled:cursor-not-allowed"
                          ><PencilLineIcon size={13} /></button>
                          {/* DP-EGSTI-ISSUE-ACTION-01 · Jun 2026 —
                              explicit Issue action. Rendered ONLY for an
                              Authorised Draft (status=Draft AND
                              approval_status ∈ {Authorized, Approved})
                              and only when the user carries the canonical
                              edit permission that /issue itself requires.
                              Hidden for Not Submitted / Rejected /
                              Pending Approval / Issued. */}
                          {canEdit && r.status === "Draft" && _authorised && (
                            <button
                              title="Issue Export GST Invoice — assign final number & post (Authorise ≠ Issue)"
                              data-testid={`exp-gst-issue-${r.id.slice(0, 8)}`}
                              onClick={() => issueRow(r)}
                              className="p-1.5 rounded-sm text-emerald-400 hover:bg-emerald-500/10 border border-emerald-500/30"
                            ><CheckCircleIcon size={13} weight="bold" /></button>
                          )}
                        </>
                      );
                    })()}
                    {r.invoice_no && (
                      <>
                        <button title="Browser Print (A4 HTML)" data-testid={`exp-gst-print-${r.id.slice(0, 8)}`} onClick={() => printHtml(r)} className="p-1.5 rounded-sm text-sky-300 hover:bg-sky-500/10 border border-sky-500/30"><PrinterIcon size={13} weight="bold" /></button>
                        <DocDownloadIcons type="EGST" id={r.id} testPrefix={`exp-gst-row-${r.id.slice(0, 8)}`} />
                        {r.status !== "Cancelled" && (
                          r.shipping_bill_no ? (
                            <span title={`Shipping Bill ${r.shipping_bill_no} · Port ${r.port_code || "—"}`}
                                  className="px-2 py-1 rounded-sm text-[10px] font-mono uppercase tracking-widest text-emerald-300 border border-emerald-500/40" data-testid={`exp-gst-sb-stamped-${r.id.slice(0, 8)}`}>SB ✓</span>
                          ) : (
                            <button title="Stamp Shipping Bill" onClick={() => openStampSb(r)} className="px-2 py-1 rounded-sm text-[10px] font-mono uppercase tracking-widest text-amber-300 border border-amber-500/40 hover:bg-amber-500/10" data-testid={`exp-gst-sb-btn-${r.id.slice(0, 8)}`}>Stamp SB</button>
                          )
                        )}
                      </>
                    )}
                    {canEdit && r.status === "Draft" && (
                      <button title="Delete draft" data-testid={`exp-gst-delete-${r.id.slice(0, 8)}`} onClick={() => removeDraft(r)} className="p-1.5 rounded-sm text-red-300 hover:bg-red-500/10"><TrashIcon size={13} /></button>
                    )}
                    {canEdit && r.status !== "Draft" && r.status !== "Cancelled" && (
                      <button title="Cancel invoice (with reason)" data-testid={`exp-gst-cancel-${r.id.slice(0, 8)}`} onClick={() => cancel(r)} className="p-1.5 rounded-sm border border-amber-500/40 text-amber-300 hover:bg-amber-500/10"><ProhibitIcon size={13} weight="bold" /></button>
                    )}
                    {/* EGSTI-MA-01B · Jun 2026 — removed the dead Admin
                        hard-delete control on numbered non-Draft EGSTIs. The
                        backend refuses hard-delete of ANY numbered document for
                        all roles (document-number integrity), so this button
                        always 400'd. Cancel is the correct disposition;
                        numbered-draft invalidation is a deferred authorised
                        phase. Backend rule UNCHANGED. */}
                    </RowActionsMenu>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {editorOpen && (
        <ExportInvoiceEditor
          initial={editing}
          forceReadOnly={viewOnly}
          onClose={() => { setEditorOpen(false); setEditing(null); setViewOnly(false); }}
          onSaved={async () => { setEditorOpen(false); setEditing(null); setViewOnly(false); await load(); }}
        />
      )}

      {/* Shipping Bill stamp dialog */}
      <Dialog open={!!sbInv} onOpenChange={(o) => !o && setSbInv(null)}>
        <DialogContent className="bg-[var(--bg-1)] border-[var(--border)] max-w-lg" data-testid="exp-gst-sb-dialog" description="Export GST Invoice action — foreign currency + IEC + shipping details.">
          <DialogHeader><DialogTitle className="font-display uppercase tracking-widest">Stamp Shipping Bill · {sbInv?.invoice_no}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3 pt-2 text-sm">
            <div className="col-span-2 text-[11px] text-amber-300 border border-amber-500/40 bg-amber-500/5 px-2 py-1.5 rounded-sm">
              Stamping a Shipping Bill locks this invoice from further amendment. Cancel + reissue is the only path after stamping.
            </div>
            <div><div className="overline mb-1">Shipping Bill No <span className="text-red-400">*</span></div>
              <input className="w-full h-9 px-2.5 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-sm font-mono" data-testid="exp-gst-sb-no"
                     value={sbForm.shipping_bill_no} onChange={e => setSbForm(p => ({ ...p, shipping_bill_no: e.target.value.toUpperCase() }))} placeholder="1234567" /></div>
            <div><div className="overline mb-1">Shipping Bill Date</div>
              <input type="date" className="w-full h-9 px-2.5 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-sm" data-testid="exp-gst-sb-date"
                     value={sbForm.shipping_bill_date} onChange={e => setSbForm(p => ({ ...p, shipping_bill_date: e.target.value }))} /></div>
            <div className="col-span-2"><div className="overline mb-1">Port Code <span className="text-red-400">*</span> <span className="text-zinc-500">(6-char ICEGATE code, e.g. INNSA1)</span></div>
              <input className="w-full h-9 px-2.5 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-sm font-mono uppercase" data-testid="exp-gst-sb-port"
                     value={sbForm.port_code} onChange={e => setSbForm(p => ({ ...p, port_code: e.target.value.toUpperCase() }))} maxLength={10} /></div>
          </div>
          <DialogFooter>
            <button onClick={() => setSbInv(null)} className="h-9 px-4 border border-[var(--border)] hover:bg-[var(--bg-2)] text-sm rounded-sm">Cancel</button>
            <button data-testid="exp-gst-sb-submit" onClick={submitStampSb} className="h-9 px-4 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold rounded-sm">Stamp & Lock</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// -----------------------------------------------------------------
//  Editor Dialog
// -----------------------------------------------------------------
function ExportInvoiceEditor({ initial, onClose, onSaved, forceReadOnly = false }) {
  const isNew = !initial;
  const savedStyles = useDocStyles(initial?.id ? "EGST" : null, initial?.id);
  const isSbStamped = !!(initial?.shipping_bill_no || "").trim();
  // DP-EGSTI-PENDING-EDIT-LOCK-01 · Mar 2026 — MA lifecycle contributes
  // to the read-only decision as defence in depth. Even if the Action
  // Menu is bypassed, editing surfaces are inert while the EGSTI is
  // Pending Approval or Management Authorised. Frontend never trusts
  // itself alone — backend PUT also rejects (see
  // export_gst_invoices.py::update_invoice).
  const _apInitial = String(initial?.approval_status || "").trim();
  const _lifecycleLock = initial && (
    _apInitial === "Pending Approval"
    || _apInitial === "Authorized"
    || _apInitial === "Approved"
  );
  const readOnly = forceReadOnly || (initial && (initial.status === "Cancelled" || isSbStamped)) || _lifecycleLock;
  const isAmendable = initial && ["Issued", "Amended"].includes(initial.status) && !isSbStamped;
  const [srcType, setSrcType] = useState(initial?.source?.type || "ECI");
  const [srcOptions, setSrcOptions] = useState([]);
  const [srcId, setSrcId] = useState(initial?.source?.id || "");
  const [busy, setBusy] = useState(false);
  const [inv, setInv] = useState(initial || {
    invoice_date: new Date().toISOString().slice(0, 10),
    source: {}, buyer: {}, consignee: {}, seller: {}, lines: [],
    currency: "USD", fx_rate_to_inr: 83.0,
    export_type: "LUT",
    // A1-D02-U02 · Nov 2026 — new EGSTI drafts now start under the
    // ECI-aligned v3 model (statutory tax-invoice: Grand Total
    // INCLUDES IGST). The backend `create_invoice` stamps this flag
    // regardless of client payload (A1-D02 Phase-2 safeguard 2).
    // Historical EGSTIs opened via `initial` keep whatever flag the
    // server returned — v1 (missing / additive_v1) and v2
    // (eci_aligned_v2) continue to render under their original
    // branches byte-for-byte.
    financial_model_version: "eci_aligned_v3",
    charges: { freight: 0, insurance: 0, packing: 0, other: 0 },
    totals: {}, notes: "",
    incoterms: "FOB",
    port_of_loading: "", port_of_discharge: "", country_of_final_destination: "",
    vessel_flight_no: "", container_no: "", seal_no: "", shipping_marks: "",
    shipping_bill_no: "", shipping_bill_date: "", port_code: "",
    buyer_po_no: "", buyer_po_date: "", pi_no: "", pi_date: "",
    terms: "Payment strictly as per agreed terms. All disputes subject to Indian jurisdiction.",
    declaration: "",
    status: "Draft",
    invoice_no: "",  // manual-entry mode writes into this on Draft
    references: { eso_no: "", epl_nos: [], eci_nos: [], buyer_po_no: "" },
  });

  // Aug 2026 · Fetch Export-GST numbering config (mirrors Domestic GST).
  const [numCfg, setNumCfg] = useState({ auto_generation: true, prefix: "", starting_sequence: 1 });
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/settings/invoice-numbering");
        setNumCfg(data?.export_gst || { auto_generation: true, prefix: "", starting_sequence: 1 });
      } catch { /* keep default = auto ON */ }
    })();
  }, []);

  const [customers, setCustomers] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/customers/");
        setCustomers(data || []);
      } catch {}
    })();
  }, []);

  useEffect(() => {
    (async () => {
      try {
        let opts = [];
        if (srcType === "ESO") {
          const { data } = await api.get("/exports/orders/");
          opts = (data || []).map(x => ({ id: x.id, no: x.doc_no, sub: x.buyer_name || "" }));
        } else if (srcType === "EPL") {
          const { data } = await api.get("/exports/packing-lists/");
          opts = (data || []).map(x => ({ id: x.id, no: x.doc_no, sub: x.export_order_no || "" }));
        } else if (srcType === "ECI") {
          // EGSTI-LINK-H01 · Phase 2 · Mar 2026 — the /eligible-eci
          // endpoint enforces the ratified eligibility contract:
          //   approval_status=Authorized, not Cancelled/Invalidated,
          //   has EPL(s), has an operational EDC, not already claimed
          //   by an active EGSTI. Fallback to the raw list if the
          //   endpoint is unavailable in a non-Phase-2 backend.
          try {
            const { data } = await api.get("/export-gst-invoices/eligible-eci");
            opts = (data || []).map(x => ({
              id: x.eci_id, no: x.eci_no,
              sub: `${x.buyer_name || ""} · ESO ${x.eso_no || "-"} · EDC ${x.edc_no || "-"} · EPL ${(x.epl_nos || []).join(", ") || "-"} · Qty ${x.invoiced_qty || 0}`,
              // Full row kept for the lineage tile below.
              _lineage: x,
            }));
          } catch {
            const { data } = await api.get("/exports/invoices/");
            opts = (data || []).map(x => ({ id: x.id, no: x.doc_no, sub: x.buyer_name || "" }));
          }
        } else if (srcType === "CUST") {
          opts = customers.filter(c => (c.country || "").toLowerCase() !== "india").map(c => ({ id: c.id, no: c.code, sub: c.name }));
        }
        setSrcOptions(opts);
      } catch { setSrcOptions([]); }
    })();
  }, [srcType, customers]);

  const prefill = async () => {
    if (!srcType || !srcId) { toast.error("Pick a source document first"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/export-gst-invoices/prefill", { src_type: srcType, src_id: srcId });
      // EGSTI-NUM-INHERITANCE · Phase 2 · Feb 2026 — ECI-sourced
      // drafts carry the inherited invoice number into the editor
      // state immediately so the locked Inherited-Invoice tile below
      // renders the final number on first paint. Backend Phase 1
      // enforces `EGSTI.invoice_no = ECI.doc_no` on the final POST.
      const _inherited_no = (data?.source?.type === "ECI" && data?.source?.no) ? data.source.no : "";
      // A1-D01 · P-γ (frozen 2026-10-07) — on an ECI-sourced prefill,
      // the backend `/prefill` is authoritative for `export_type` and
      // `charges`. The pre-fix merge `prev.export_type || data.export_type || "LUT"`
      // short-circuited on the seeded initial state `"LUT"` and
      // discarded the correct IGST value from the backend (proven
      // defect site D01-A/B in /app/memory/A1_D01_EGSTI_GST_Inheritance_RCA.md §4).
      // On non-ECI sources (ESO, EPL, CUST) the historical merge
      // precedence is preserved — only the ECI branch is tightened.
      const _ecisrc = data?.source?.type === "ECI";
      setInv(prev => ({
        ...prev, ...data,
        export_type: _ecisrc
          ? (data.export_type || "LUT")
          : (prev.export_type || data.export_type || "LUT"),
        charges: _ecisrc
          ? (data.charges || prev.charges)
          : (prev.charges || data.charges),
        notes: prev.notes, terms: prev.terms,
        status: prev.status || "Draft",
        // Inherited customer-facing invoice number (ECI-sourced only).
        invoice_no: _inherited_no || prev.invoice_no || "",
        // A1-D02 · Nov 2026 — Owner Phase-2 safeguard 1: ECI prefill
        // MUST NOT reset or remove the new-model designation from a
        // new EGSTI draft. The prefill response never carries this
        // field, so we always preserve the draft's existing value.
        financial_model_version: prev.financial_model_version || data.financial_model_version || "eci_aligned_v3",
      }));
      toast.success(`Prefilled ${data.lines?.length || 0} line(s) from ${srcType} · ${data.source?.no || ""}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Prefill failed");
    } finally { setBusy(false); }
  };

  const totals = useMemo(() => {
    const withIgst = inv.export_type === "IGST";
    let taxable = 0, igst = 0, disc = 0;
    for (const ln of (inv.lines || [])) {
      const qty = Number(ln.qty) || 0;
      const rate = Number(ln.rate) || 0;
      const gross = qty * rate;
      const dA = gross * (Number(ln.discount_pct) || 0) / 100;
      const tx = gross - dA;
      taxable += tx; disc += dA;
      if (withIgst) igst += tx * (Number(ln.gst_rate) || 0) / 100;
    }
    const freight = Number(inv.charges?.freight) || 0;
    const insurance = Number(inv.charges?.insurance) || 0;
    const packing = Number(inv.charges?.packing) || 0;
    const other = Number(inv.charges?.other) || 0;
    // A1-D02 · Nov 2026 — mirror backend `_recompute_totals` branching.
    // Historical EGSTIs opened in View (`additive_v1` / missing flag)
    // keep the legacy additive formula so numbers match the saved
    // record byte-for-byte. New drafts (`eci_aligned_v2`) honour the
    // Owner-approved non-additive identity.
    // A1-D02-U02 · Nov 2026 — three-way branch mirrors backend:
    //   v1  (additive_v1)   → legacy additive formula
    //   v2  (eci_aligned_v2) → grand = taxable (IGST NOT added)
    //   v3  (eci_aligned_v3) → grand = taxable + igst (IGST included
    //                          exactly once; statutory tax invoice)
    const model = (inv.financial_model_version || "additive_v1").trim() || "additive_v1";
    const isV2 = model === "eci_aligned_v2";
    const isV3 = model === "eci_aligned_v3";
    const grand_fc = isV3
      ? (taxable + igst)                                        // ECI-aligned v3 (incl. IGST)
      : (isV2
          ? taxable                                             // ECI-aligned v2 (excl. IGST)
          : (taxable + igst + freight + insurance + packing + other)); // additive_v1
    const fob = (isV2 || isV3) ? (taxable - freight - insurance) : taxable;
    const fx = Number(inv.fx_rate_to_inr) || 0;
    const grand_inr = fx ? grand_fc * fx : 0;
    const total_igst_inr = fx ? igst * fx : 0;
    return { taxable_value: taxable, total_discount: disc, total_igst: igst,
             total_igst_inr,
             freight, insurance, packing, other,
             fob_value: fob,
             grand_total_fc: grand_fc, grand_total_inr: grand_inr,
             // A1-D02-U01 · Nov 2026 · Owner-approved Entry Totals
             // particulars (presentation-only derivations; nothing
             // persisted). All derivations are v2-safe: they never
             // alter the backend-authoritative `grand_total_fc` or
             // historical additive_v1 reads.
             _u01: (() => {
               const _lineTotal = taxable;                     // Σ line values (post-discount)
               const _summaryDiscount = -(freight + insurance);
               const _totalTaxable = _lineTotal + freight + insurance + _summaryDiscount;
               // IGST uniformity (Owner D3). Prefer the authoritative
               // top-level `gst_treatment` / `gst_rate_pct` inherited
               // from the source ECI. Fall back to distinct non-FOC
               // per-line rates for legacy EGSTIs that pre-date
               // A1-D03 P2.5-U01. FOC lines are excluded from the
               // distinct-rates computation (their forced
               // `gst_rate=0` was previously poisoning the set and
               // forcing "— (mixed)" on single-rate invoices).
               const _nonFocLines = (inv.lines || []).filter(l =>
                 (String(l.line_type || "Normal").trim() || "Normal") !== "FOC");
               const _rates = _nonFocLines.map(l => Number(l.gst_rate) || 0);
               const _distinctRates = Array.from(new Set(_rates));
               const _uniform = _distinctRates.length <= 1;
               const _savedRatePct = (inv.gst_rate_pct !== undefined && inv.gst_rate_pct !== null && !isNaN(Number(inv.gst_rate_pct)))
                 ? Number(inv.gst_rate_pct) : null;
               const _headerIgstRate = (_savedRatePct !== null && withIgst)
                 ? _savedRatePct
                 : ((withIgst && _uniform) ? (_distinctRates[0] || 0) : null);
               const _igstMixed = (_savedRatePct !== null) ? false : (withIgst && !_uniform);
               const _igstUsd = igst;
               const _igstInr = total_igst_inr;
               // A1-D02-U02 · Nov 2026 — Grand Total INCLUDES IGST
               // under v3 only. v2 preserves the exclusive semantic.
               const _grandUsd = isV3 ? (_totalTaxable + _igstUsd) : _totalTaxable;
               const _grandInr = fx ? _grandUsd * fx : 0;
               return {
                 line_total_value: _lineTotal,
                 summary_discount: _summaryDiscount,
                 total_taxable_value: _totalTaxable,
                 igst_rate_pct_header: _headerIgstRate,
                 igst_rate_mixed: _igstMixed,
                 igst_value_usd: _igstUsd,
                 igst_value_inr: _igstInr,
                 grand_total_usd_ex_igst: _grandUsd,   // name kept for backward testid compat
                 grand_total_inr_ex_igst: _grandInr,
                 grand_total_includes_igst: isV3,
                 fx_present: !!fx,
               };
             })(),
           };
  }, [inv.lines, inv.charges, inv.export_type, inv.fx_rate_to_inr, inv.financial_model_version, inv.gst_treatment, inv.gst_rate_pct]);

  const patchLine = (idx, patch) => {
    setInv(prev => {
      const lines = [...(prev.lines || [])];
      lines[idx] = { ...lines[idx], ...patch };
      return { ...prev, lines };
    });
  };
  const addLine = () => setInv(prev => ({
    ...prev,
    lines: [...(prev.lines || []), { line_no: (prev.lines?.length || 0) + 1, article_no: "", style_no: "", description: "", hsn_code: "", unit: "MTR", qty: 0, rate: 0, discount_pct: 0, gst_rate: 0 }],
  }));
  const removeLine = (idx) => setInv(prev => ({ ...prev, lines: (prev.lines || []).filter((_, i) => i !== idx).map((l, i) => ({ ...l, line_no: i + 1 })) }));

  // DP-EGSTI-BLANK-SAVE-01 · Mar 2026 — Save validation. Prevents a
  // blank/incomplete New EGSTI from wasting a sequence number and from
  // showing a stale "Draft saved" toast. Backend performs the same
  // check independently; frontend is convenience only.
  const _validateForSave = () => {
    const _srcType = String(inv?.source?.type || "").toUpperCase();
    const _srcId = String(inv?.source?.id || "").trim();
    if (_srcType !== "ECI" || !_srcId) {
      return "Select an Export Commercial Invoice before saving.";
    }
    if (!String(inv?.buyer?.name || "").trim()) {
      return "Buyer / foreign customer name is required.";
    }
    const lines = inv?.lines || [];
    if (lines.length === 0) {
      return "Invoice must have at least one line item.";
    }
    for (let i = 0; i < lines.length; i++) {
      const ln = lines[i];
      const hasId = !!(ln.product_id || ln.article_id || ln.line_id || ln.article_no);
      if (!hasId) return `Line ${i + 1}: missing product / article identity.`;
      const qty = Number(ln.qty || 0);
      if (!Number.isFinite(qty) || qty <= 0) {
        return `Line ${i + 1}: quantity must be greater than zero.`;
      }
      if (!String(ln.unit || ln.uom || "").trim()) {
        return `Line ${i + 1}: unit of measure is required.`;
      }
    }
    return null;
  };

  const saveDraft = async () => {
    if (isNew) {
      const _err = _validateForSave();
      if (_err) { toast.error(_err); return; }
    }
    setBusy(true);
    try {
      if (isNew) {
        const { data } = await api.post("/export-gst-invoices/", inv);
        toast.success("Draft saved");
        setInv(data);
      } else {
        const { data } = await api.put(`/export-gst-invoices/${initial.id}`, inv);
        toast.success("Draft updated");
        setInv(data);
      }
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };

  const issue = async () => {
    if (isNew) {
      const _err = _validateForSave();
      if (_err) { toast.error(_err); return; }
    }
    setBusy(true);
    try {
      let id = initial?.id;
      if (isNew) {
        const { data } = await api.post("/export-gst-invoices/", inv);
        id = data.id; setInv(data);
      } else if (initial.status === "Draft") {
        const { data } = await api.put(`/export-gst-invoices/${id}`, inv);
        setInv(data);
      }
      const { data: issued } = await api.post(`/export-gst-invoices/${id}/issue`);
      toast.success(`${issued.invoice_no} · issued`);
      onSaved && await onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Issue failed"); }
    finally { setBusy(false); }
  };

  // DP-EGSTI-LIFECYCLE-UI-UNIFORMITY-01 · Jun 2026 — View-popup Issue.
  // Direct canonical POST /issue (no PUT — an Authorised Draft is
  // edit-locked, so there is nothing to persist first). Mirrors the
  // register row action exactly; both surfaces hit the SAME endpoint.
  const issueAuthorised = async () => {
    if (!window.confirm(`Issue ${inv.invoice_no || "this Authorised Draft"}?\n\nAuthorise ≠ Issue — this is the explicit Issue step.\nDocument Status becomes Issued; Approval Status stays Authorized.`)) return;
    setBusy(true);
    try {
      const { data: issued } = await api.post(`/export-gst-invoices/${initial.id}/issue`);
      toast.success(`${issued.invoice_no} · issued`);
      onSaved && await onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Issue failed"); }
    finally { setBusy(false); }
  };

  const amend = async () => {
    const reason = window.prompt(`Amend ${initial.invoice_no}?\n\nEnter reason (min 4 chars, kept in audit trail):`);
    if (!reason || reason.trim().length < 4) { if (reason !== null) toast.error("Reason must be ≥ 4 chars"); return; }
    setBusy(true);
    try {
      const updates = {
        lines: inv.lines, charges: inv.charges, buyer: inv.buyer, consignee: inv.consignee,
        notes: inv.notes, terms: inv.terms, declaration: inv.declaration,
        buyer_po_no: inv.buyer_po_no, buyer_po_date: inv.buyer_po_date,
        pi_no: inv.pi_no, pi_date: inv.pi_date,
        incoterms: inv.incoterms, currency: inv.currency, fx_rate_to_inr: inv.fx_rate_to_inr,
        export_type: inv.export_type,
        port_of_loading: inv.port_of_loading, port_of_discharge: inv.port_of_discharge,
        country_of_final_destination: inv.country_of_final_destination,
        vessel_flight_no: inv.vessel_flight_no, container_no: inv.container_no,
        seal_no: inv.seal_no, shipping_marks: inv.shipping_marks,
        invoice_date: inv.invoice_date, references: inv.references,
      };
      const { data } = await api.post(`/export-gst-invoices/${initial.id}/amend`, {
        reason: reason.trim(), updates,
      });
      toast.success(`${data.invoice_no} · amended`);
      onSaved && await onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Amend failed"); }
    finally { setBusy(false); }
  };

  const withIgst = inv.export_type === "IGST";
  const canIssue = !readOnly && (inv.lines?.length || 0) > 0 && !!inv.buyer?.name;
  // EGSTI-MA-H01 · Phase 2 · Mar 2026 — MA-driven action gating.
  //   Not Submitted / blank / Rejected  → Submit for Approval
  //   Pending Approval                  → Authorise / Reject (via ApprovalControls)
  //   Authorized / Approved             → Issue when otherwise eligible
  const _mgmtStatus = String(inv?.approval_status || "").trim();
  const isNotSubmitted = _mgmtStatus === "" || _mgmtStatus === "Not Submitted";
  const isRejected = _mgmtStatus === "Rejected";
  const isPendingMA = _mgmtStatus === "Pending Approval";
  const isAuthorised = _mgmtStatus === "Authorized" || _mgmtStatus === "Approved";
  const canSubmitForApproval = !readOnly && (isNew || inv.status === "Draft") && (isNotSubmitted || isRejected) && (inv.lines?.length || 0) > 0 && !!inv?.buyer?.name;
  // Phase 1B.1 · split visibility from enabled-state so Submit for Authorisation
  // is always visible on New/Draft (and Rejected) even when buyer/line missing.
  const showSubmitForApproval = !readOnly && canSubmitDoc("EGST")
    && (isNew || inv.status === "Draft")
    && (isNotSubmitted || isRejected)
    && !isAmendable;
  const submitReqsMet = (inv.lines?.length || 0) > 0 && !!inv?.buyer?.name;
  const enableSubmitForApproval = showSubmitForApproval && submitReqsMet;
  const submitDisabledReason = !submitReqsMet
    ? "Add buyer and at least one line to submit for authorisation"
    : "";
  const issueBlockedByMA = !isAuthorised && !isAmendable;
  // DP-EGSTI-LIFECYCLE-UI-UNIFORMITY-01 · Jun 2026 — shared Issue
  // eligibility, identical to the register row Action Menu predicate:
  // a saved Authorised Draft (status=Draft AND approval_status ∈
  // {Authorized, Approved}) with lines + buyer. Deliberately independent
  // of `readOnly` so the explicit Issue action also surfaces in the
  // read-only View popup. Row action and View action share this gate and
  // call the SAME canonical /issue endpoint.
  const issueEligible = !!initial?.id
    && initial.status === "Draft"
    && isAuthorised
    && (inv.lines?.length || 0) > 0
    && !!inv.buyer?.name;
  const ccy = (inv.currency || "USD").toUpperCase();

  const submitForApproval = async () => {
    if (!window.confirm(`Submit ${inv.invoice_no || "this Draft"} for Management Authorisation?\n\nOnce submitted, editing is frozen until Authorise / Reject.`)) return;
    setBusy(true);
    try {
      // Phase 1B persist-then-submit: create when new, update when Draft.
      let id = inv?.id;
      if (isNew) {
        const { data } = await api.post("/export-gst-invoices/", inv);
        setInv(data); id = data.id;
      } else if (initial?.id && initial.status === "Draft") {
        const { data } = await api.put(`/export-gst-invoices/${inv.id}`, inv);
        setInv(data); id = data.id;
      }
      try {
        const { data } = await api.post(`/export-gst-invoices/${id}/submit-for-approval`, {});
        setInv(data);
      } catch (se) {
        toast.error("Invoice was saved as Draft, but Submit for Authorisation failed. You can submit it from the list.");
        onSaved && await onSaved({ silent: true });
        return;
      }
      toast.success(`${inv.invoice_no || "Draft"} · Submitted for Authorisation`);
      onSaved && await onSaved({ silent: true });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Submit for Authorisation failed");
    } finally { setBusy(false); }
  };


  return (
    <Dialog open onOpenChange={(v) => !v && !busy && onClose()}>
      <DialogContent
        data-testid="exp-gst-editor-dialog"
        className="bg-[var(--bg-1)] border-[var(--border)] text-white max-w-[1240px] max-h-[92vh] overflow-y-auto"
        onInteractOutside={(e) => e.preventDefault()}
        description="Export GST Invoice action — foreign currency + IEC + shipping details."
      >
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-3">
            <GlobeIcon size={18} weight="bold" className="text-[var(--accent)]" />
            {isNew ? "New Export GST Invoice" : `Invoice ${inv.invoice_no || "(draft)"}`}
            <span className={`ml-2 inline-flex items-center px-2 py-0.5 rounded-sm border text-[10px] font-mono uppercase tracking-widest ${STATUS_STYLE[inv.status] || ""}`}>
              {inv.status}
            </span>
            <span className={`ml-1 inline-flex items-center px-2 py-0.5 rounded-sm border text-[10px] font-mono uppercase tracking-widest ${
              withIgst ? "border-amber-500/40 text-amber-300" : "border-emerald-500/40 text-emerald-300"
            }`}>
              {withIgst ? "IGST paid" : "LUT · 0%"}
            </span>
          </DialogTitle>
        </DialogHeader>
        {/* Phase 1B.1 · Scoped top-right Close chip — same remediation as
            PI/EPI/GST. */}
        <button
          type="button"
          data-testid="exp-gst-close-top"
          onClick={() => !busy && onClose()}
          aria-label="Close dialog"
          className="absolute right-10 top-3 z-10 h-8 px-2.5 inline-flex items-center gap-1.5 rounded-sm border border-[var(--border)] text-zinc-300 text-[11px] font-mono uppercase tracking-widest hover:bg-white/10 disabled:opacity-40"
          disabled={busy}
        >
          <XIcon size={12} weight="bold" /> Close
        </button>

        {/* Mar 1, 2026 · Per-doc Approval Workflow (Admin/Manager only) */}
        {inv?.id && (
          <ApprovalControls
            docType="egst"
            docId={inv.id}
            docNo={inv.invoice_no || `(draft ${inv.id.slice(0, 8)})`}
            approvalStatus={inv.approval_status}
            rejectionReason={inv.rejection_reason}
            approvalHistory={inv.approval_history}
            onChange={async () => {
              try {
                const { data } = await api.get(`/export-gst-invoices/${inv.id}`);
                setInv(data);
                onSaved && await onSaved({ silent: true });
              } catch {}
            }}
            testIdPrefix="exp-gst-approval"
          />
        )}

        {inv?.id && (
          <DocumentInfo docNo={inv.invoice_no} createdAt={inv.created_at} updatedAt={inv.updated_at} createdBy={inv.created_by} docLabel="Invoice No." testidPrefix="exp-gst" />
        )}

        {/* Source picker */}
        {!readOnly && (
          <div className="grid grid-cols-12 gap-3 border border-[var(--border)] rounded-sm p-3 bg-[var(--bg-2)]/30">
            <label className="col-span-2 flex items-center text-[11px] uppercase tracking-widest text-zinc-400">Source Type</label>
            <select
              data-testid="exp-gst-src-type"
              value={srcType}
              onChange={e => { setSrcType(e.target.value); setSrcId(""); }}
              className="col-span-2 h-9 px-2 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-sm"
            >
              <option value="ESO">Export Sales Order</option>
              <option value="EPL">Export Packing List</option>
              <option value="ECI">Export Invoice (ECI)</option>
              <option value="CUST">Direct Foreign Buyer</option>
            </select>
            <select
              data-testid="exp-gst-src-id"
              value={srcId}
              onChange={e => setSrcId(e.target.value)}
              className="col-span-6 h-9 px-2 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-sm"
            >
              <option value="">— pick a {srcType === "CUST" ? "foreign customer" : srcType} —</option>
              {srcOptions.map(o => (
                <option key={o.id} value={o.id}>{o.no} {o.sub ? `· ${o.sub}` : ""}</option>
              ))}
            </select>
            <button
              data-testid="exp-gst-prefill-btn"
              disabled={busy || !srcId}
              onClick={prefill}
              className="col-span-2 h-9 border border-[var(--accent)] text-[var(--accent)] hover:bg-[var(--accent)]/10 text-xs font-mono uppercase tracking-widest rounded-sm disabled:opacity-40"
            >Prefill</button>
          </div>
        )}

        {/* EGSTI-LINK-H01 · Phase 2 · Mar 2026 — read-only source
            lineage tiles. Populated from `references` set by
            `_prefill_from_eci` and refreshed live at View load via
            `linked_edc_no` (sealed operational-EDC resolver). Never
            surfaces a reversed / void EDC. */}
        {(inv?.references?.eci_no || inv?.linked_eci_no || inv?.references?.eso_no) && (
          <div className="grid grid-cols-4 gap-3 border border-[var(--border)] rounded-sm p-3 bg-[var(--bg-2)]/20">
            <div data-testid={`egsti-lineage-eso`}>
              <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-400 mb-1">Linked Export SO</div>
              <div className="font-mono text-sm">{inv?.linked_eso_no || inv?.references?.eso_no || "—"}</div>
            </div>
            <div data-testid={`egsti-lineage-epl`}>
              <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-400 mb-1">Linked Packing List(s)</div>
              <div className="font-mono text-sm">{(inv?.linked_epl_nos || inv?.references?.epl_nos || []).join(", ") || "—"}</div>
            </div>
            <div data-testid={`egsti-lineage-edc`}>
              <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-400 mb-1">Linked Delivery Challan</div>
              <div className="font-mono text-sm">{inv?.linked_edc_no || inv?.references?.edc_no || "—"}</div>
            </div>
            <div data-testid={`egsti-lineage-eci`}>
              <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-400 mb-1">Linked Commercial Invoice</div>
              <div className="font-mono text-sm">{inv?.linked_eci_no || inv?.references?.eci_no || "—"}</div>
            </div>
          </div>
        )}

        {/* EGSTI-NUM-INHERITANCE · Phase 2 · Feb 2026 — Inherited
            Invoice Number tile. ECI is the master export invoice
            number owner; the EGSTI inherits the ECI's doc_no verbatim
            (backend Phase 1 enforced). Rendered as a locked read-only
            display both in New (after prefill) and in Edit/View. The
            field is intentionally not an input — the user cannot type,
            override, regenerate, or clear it. */}
        {(inv?.references?.eci_no || inv?.linked_eci_no || inv?.invoice_no) && (
          <div className="tactical-card p-3 flex flex-wrap items-center gap-4 border-emerald-500/40 bg-emerald-500/5"
               data-testid="egsti-inherited-invoice-strip">
            <span className="text-[10px] font-mono uppercase tracking-widest text-emerald-300 px-2 py-0.5 border border-emerald-500/40 rounded-sm">
              Inherited from ECI
            </span>
            <div className="flex items-baseline gap-2">
              <label className="text-[11px] uppercase tracking-widest text-zinc-400">Source ECI:</label>
              <span className="font-mono text-sm text-zinc-200" data-testid="egsti-source-eci-no">
                {inv?.references?.eci_no || inv?.linked_eci_no || "—"}
              </span>
            </div>
            <div className="flex items-baseline gap-2">
              <label className="text-[11px] uppercase tracking-widest text-zinc-400">Export GST Invoice No:</label>
              <span className="font-mono text-sm text-emerald-300" data-testid="egsti-inherited-invoice-no">
                {inv?.invoice_no || inv?.references?.eci_no || inv?.linked_eci_no || "—"}
              </span>
            </div>
            <span className="ml-auto text-[10px] font-mono text-zinc-500" title="Backend-enforced: invoice_no = source ECI.doc_no (immutable)">
              Locked · immutable
            </span>
          </div>
        )}

        {/* Aug 2026 · Manual invoice-number strip (Export GST). Shown
            only when the admin has switched Export-GST numbering to
            manual AND the current document is NOT ECI-sourced. ECI
            inheritance (Feb 2026 Phase 2) always wins — the manual
            toggle is ignored for ECI-sourced EGSTIs. */}
        {!numCfg.auto_generation
          && !(inv?.source?.type === "ECI" || inv?.references?.eci_id || inv?.linked_eci_id) && (
          <div className="tactical-card p-3 flex items-center gap-3 border-amber-500/40 bg-amber-500/5">
            <span className="text-[10px] font-mono uppercase tracking-widest text-amber-300 px-2 py-0.5 border border-amber-500/40 rounded-sm">Manual mode</span>
            <label className="text-xs text-zinc-400 whitespace-nowrap">Invoice Number *</label>
            <input
              data-testid="exp-gst-manual-invoice-no"
              disabled={readOnly || (inv.status && inv.status !== "Draft")}
              value={inv.invoice_no || ""}
              onChange={e => setInv(prev => ({ ...prev, invoice_no: e.target.value }))}
              placeholder="e.g. EXP-2627-500"
              className="flex-1 h-9 px-2 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-sm font-mono focus:outline-none focus:border-[var(--accent)]"
            />
            <span className="text-[10px] font-mono text-zinc-500">Duplicate-checked across Domestic + Export</span>
          </div>
        )}

        {/* Header — Invoice meta */}
        <div className="grid grid-cols-4 gap-3">
          <Field label="Invoice Date" testid="exp-gst-date">
            <input type="date" disabled={readOnly} value={inv.invoice_date || ""} onChange={e => setInv(prev => ({ ...prev, invoice_date: e.target.value }))} className="w-full h-9 px-2 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-sm" />
          </Field>
          <Field label="Incoterms">
            <select disabled={readOnly} value={inv.incoterms || "FOB"} onChange={e => setInv(prev => ({ ...prev, incoterms: e.target.value }))} className="w-full h-9 px-2 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-sm" data-testid="exp-gst-incoterms">
              {["FOB", "CIF", "EXW", "DDP", "DAP", "CFR", "FCA", "CIP"].map(i => <option key={i}>{i}</option>)}
            </select>
          </Field>
          <Field label="Currency">
            <select disabled={readOnly} value={ccy} onChange={e => setInv(prev => ({ ...prev, currency: e.target.value }))} className="w-full h-9 px-2 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-sm font-mono" data-testid="exp-gst-currency">
              {CCY_OPTIONS.map(c => <option key={c}>{c}</option>)}
            </select>
          </Field>
          <Field label="FX Rate → INR">
            <input type="number" step="0.01" disabled={readOnly} value={inv.fx_rate_to_inr ?? 83} onChange={e => setInv(prev => ({ ...prev, fx_rate_to_inr: e.target.value }))} className="w-full h-9 px-2 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-sm font-mono text-right tabular-nums" data-testid="exp-gst-fx" />
          </Field>
        </div>

        {/* Export type toggle */}
        <div className="border border-[var(--accent)]/40 rounded-sm p-3 bg-[var(--accent)]/5 flex items-center gap-4 flex-wrap">
          <div className="text-[11px] uppercase tracking-widest text-zinc-400">GST Treatment</div>
          <div className="flex gap-2" data-testid="exp-gst-type-toggle">
            <button type="button" disabled={readOnly || !!inv.gst_treatment} onClick={() => setInv(prev => ({ ...prev, export_type: "LUT" }))}
              data-testid="exp-gst-type-lut"
              className={`px-3 py-1.5 rounded-sm text-xs font-mono uppercase tracking-widest border ${
                inv.export_type === "LUT" ? "border-emerald-500 bg-emerald-500/10 text-emerald-300" : "border-[var(--border)] text-zinc-400"
              } ${(readOnly || !!inv.gst_treatment) ? "opacity-60 cursor-not-allowed" : ""}`}>Export under LUT / Bond · 0%</button>
            <button type="button" disabled={readOnly || !!inv.gst_treatment} onClick={() => setInv(prev => ({ ...prev, export_type: "IGST" }))}
              data-testid="exp-gst-type-igst"
              className={`px-3 py-1.5 rounded-sm text-xs font-mono uppercase tracking-widest border ${
                inv.export_type === "IGST" ? "border-amber-500 bg-amber-500/10 text-amber-300" : "border-[var(--border)] text-zinc-400"
              } ${(readOnly || !!inv.gst_treatment) ? "opacity-60 cursor-not-allowed" : ""}`}>With Payment of IGST</button>
          </div>
          {/* A1-D03 · P2.5-U01 · Feb 2026 — GST Applicable (read-only
              for ECI-linked EGSTIs). Mirrors the ECI GST Treatment
              field options verbatim; the value is inherited and
              cannot drift from the authorised source ECI. */}
          <div className="flex items-center gap-2" data-testid="exp-gst-applicable-wrap">
            <div className="text-[11px] uppercase tracking-widest text-zinc-400">GST Applicable</div>
            <select
              data-testid="exp-gst-applicable"
              disabled
              value={String(inv.gst_treatment || "").toUpperCase() || (inv.export_type === "LUT" ? "LUT" : "")}
              className="h-8 px-2 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-xs font-mono uppercase tracking-widest text-zinc-200 opacity-90 cursor-not-allowed"
              title="Inherited from source ECI · cannot be edited on an ECI-linked EGSTI"
            >
              {!inv.gst_treatment && <option value="">—</option>}
              {EGSTI_GST_TYPES.map(o => <option key={o.v} value={o.v}>{o.label}</option>)}
            </select>
            <span className="text-[9px] uppercase tracking-widest text-zinc-500" data-testid="exp-gst-applicable-source">
              Inherited from ECI
            </span>
          </div>
          <div className="text-[10px] text-zinc-500 flex-1">
            {inv.export_type === "IGST"
              ? "Applies picked IGST slab (per line) — refund claimed via GSTR-1 Table 6A."
              : "Zero-rated supply under LUT/Bond. Ensure LUT No + Date are set in Company Profile."}
          </div>
        </div>

        {/* PO / PI refs */}
        <div className="grid grid-cols-4 gap-3">
          <Field label="Buyer PO No" testid="exp-gst-po-no"><input disabled={readOnly} value={inv.buyer_po_no || ""} onChange={e => setInv(prev => ({ ...prev, buyer_po_no: e.target.value }))} className="w-full h-9 px-2 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-sm font-mono" /></Field>
          <Field label="Buyer PO Date"><input type="date" disabled={readOnly} value={inv.buyer_po_date || ""} onChange={e => setInv(prev => ({ ...prev, buyer_po_date: e.target.value }))} className="w-full h-9 px-2 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-sm" /></Field>
          <Field label="Proforma Invoice No"><input disabled={readOnly} value={inv.pi_no || ""} onChange={e => setInv(prev => ({ ...prev, pi_no: e.target.value }))} className="w-full h-9 px-2 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-sm font-mono" /></Field>
          <Field label="PI Date"><input type="date" disabled={readOnly} value={inv.pi_date || ""} onChange={e => setInv(prev => ({ ...prev, pi_date: e.target.value }))} className="w-full h-9 px-2 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-sm" /></Field>
        </div>

        {/* Buyer / Consignee */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <Party title="Seller" party={inv.seller} />
          <PartyEditable
            title="Buyer (Bill To)"
            testId="exp-gst-buyer"
            readOnly={readOnly}
            party={{ name: inv.buyer?.name || "", address: inv.buyer?.billing_address || inv.buyer?.address || "", country: inv.buyer?.country || "", tax_id: inv.buyer?.tax_id || "" }}
            onChange={(patch) => setInv(prev => ({ ...prev, buyer: { ...(prev.buyer || {}), name: patch.name, billing_address: patch.address, country: patch.country, tax_id: patch.tax_id } }))}
          />
          <PartyEditable
            title="Consignee (Ship To)"
            testId="exp-gst-consignee"
            readOnly={readOnly}
            party={{ name: inv.consignee?.name || "", address: inv.consignee?.address || "", country: inv.consignee?.country || "" }}
            hint={!inv.consignee?.address ? "Same as Buyer (edit to override)" : ""}
            onCopyFromBill={() => setInv(prev => ({ ...prev, consignee: {
              name: prev.buyer?.name || "", address: prev.buyer?.billing_address || prev.buyer?.address || "",
              country: prev.buyer?.country || "",
            } }))}
            onChange={(patch) => setInv(prev => ({ ...prev, consignee: { ...(prev.consignee || {}), name: patch.name, address: patch.address, country: patch.country } }))}
          />
        </div>

        {/* Shipping details */}
        <div className="grid grid-cols-4 gap-3 border border-[var(--accent)]/30 rounded-sm p-3 bg-[var(--accent)]/5">
          <Field label="Port of Loading" testid="exp-gst-pol"><input disabled={readOnly} value={inv.port_of_loading || ""} onChange={e => setInv(prev => ({ ...prev, port_of_loading: e.target.value }))} className="w-full h-9 px-2 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-sm" placeholder="Nhava Sheva" /></Field>
          <Field label="Port of Discharge"><input disabled={readOnly} value={inv.port_of_discharge || ""} onChange={e => setInv(prev => ({ ...prev, port_of_discharge: e.target.value }))} className="w-full h-9 px-2 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-sm" placeholder="Hamburg" /></Field>
          <Field label="Country of Destination"><input disabled={readOnly} value={inv.country_of_final_destination || ""} onChange={e => setInv(prev => ({ ...prev, country_of_final_destination: e.target.value }))} className="w-full h-9 px-2 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-sm" placeholder="GERMANY" /></Field>
          <Field label="Vessel / Flight No"><input disabled={readOnly} value={inv.vessel_flight_no || ""} onChange={e => setInv(prev => ({ ...prev, vessel_flight_no: e.target.value }))} className="w-full h-9 px-2 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-sm" /></Field>
          <Field label="Container No"><input disabled={readOnly} value={inv.container_no || ""} onChange={e => setInv(prev => ({ ...prev, container_no: e.target.value }))} className="w-full h-9 px-2 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-sm font-mono" /></Field>
          <Field label="Seal No"><input disabled={readOnly} value={inv.seal_no || ""} onChange={e => setInv(prev => ({ ...prev, seal_no: e.target.value }))} className="w-full h-9 px-2 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-sm font-mono" /></Field>
          <Field label="Shipping Marks"><input disabled={readOnly} value={inv.shipping_marks || ""} onChange={e => setInv(prev => ({ ...prev, shipping_marks: e.target.value }))} className="w-full h-9 px-2 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-sm" /></Field>
        </div>

        {/* Lines */}
        <div className="border border-[var(--border)] rounded-sm overflow-x-auto">
          <div className="flex items-center justify-between px-3 py-2 bg-[var(--bg-2)]/40">
            <div className="text-[11px] font-mono uppercase tracking-widest text-zinc-400">Line Items · {ccy}</div>
            {!readOnly && (
              <button data-testid="exp-gst-add-line" onClick={addLine} className="text-xs px-2 py-1 border border-[var(--accent)] text-[var(--accent)] hover:bg-[var(--accent)]/10 rounded-sm flex items-center gap-1"><PlusIcon size={11} /> Add Line</button>
            )}
          </div>
          <table className="w-full text-xs">
            <thead className="bg-[var(--bg-2)]/40 text-[10px] uppercase tracking-widest text-zinc-500">
              <tr>
                <th className="px-2 py-2 text-left w-10">#</th>
                <th className="px-2 py-2 text-left">Product Code / Description</th>
                <th className="px-2 py-2 text-left w-24">Style No</th>
                <th className="px-2 py-2 text-left w-24">HSN</th>
                <th className="px-2 py-2 text-right w-20">Qty</th>
                <th className="px-2 py-2 text-left w-16">Unit</th>
                <th className="px-2 py-2 text-right w-24">Rate ({ccy})</th>
                <th className="px-2 py-2 text-right w-16">Disc%</th>
                {/* A1-D02-U01 · Nov 2026 · Owner directive §2 — the
                    item-wise IGST% column is removed from the visible
                    grid. Underlying `ln.gst_rate` data is preserved
                    (used by the backend/`_recompute_totals` and the
                    header-level IGST summary row). */}
                <th className="px-2 py-2 text-right w-24">Taxable</th>
                <th className="px-2 py-2 text-right w-24">Total ({ccy})</th>
                {!readOnly && <th className="w-10"></th>}
              </tr>
            </thead>
            <tbody>
              {(inv.lines || []).length === 0 ? (
                <tr><td colSpan={11} className="px-3 py-6 text-center text-zinc-500 text-xs">No line items — use Prefill above or click Add Line.</td></tr>
              ) : inv.lines.map((ln, idx) => {
                const qty = Number(ln.qty) || 0, rate = Number(ln.rate) || 0;
                const gross = qty * rate;
                const taxable = gross * (1 - (Number(ln.discount_pct) || 0) / 100);
                const igst = withIgst ? taxable * (Number(ln.gst_rate) || 0) / 100 : 0;
                // A1-D02-U01 · Nov 2026 · Owner D4 — under both
                // ECI-aligned v2 and v3 models the per-line Total
                // excludes IGST (IGST is a separately disclosed
                // statutory charge aggregated at the header). Only
                // historical `additive_v1` keeps the legacy
                // `taxable + igst` so the on-screen Total matches the
                // stored additive grand-total build-up byte-for-byte.
                const _lineModel = (inv.financial_model_version || "additive_v1").trim() || "additive_v1";
                const _lineIsEciAligned = _lineModel === "eci_aligned_v2" || _lineModel === "eci_aligned_v3";
                const total = _lineIsEciAligned ? taxable : (taxable + igst);
                // A1-D03 P2.5 · Feb 2026 — Normal/FOC marker on the
                // EGSTI Entry/View line grid. Mirrors the Owner-
                // approved ECI entry presentation and the EGSTI v3
                // Browser Print output: Product Code only (no
                // duplicate Description), compact amber FOC chip
                // beside the code, and secondary "FOC — <reason>"
                // line within the same cell. Historical lines
                // without `line_type` default to Normal visually.
                const _lineType = (ln.line_type || "Normal");
                const _isFoc = _lineType === "FOC";
                return (
                  <tr key={idx} data-testid={`exp-gst-line-row-${idx}`} className={"border-t border-[var(--border)] " + (_isFoc ? "bg-amber-500/5" : "")}>
                    <td className="px-2 py-1 text-zinc-500 font-mono">{idx + 1}</td>
                    <td className="px-2 py-1">
                      <div className="flex items-center gap-1">
                        <input disabled={readOnly} data-testid={`exp-gst-line-article-${idx}`} value={ln.article_no || ""} onChange={e => patchLine(idx, { article_no: e.target.value })} placeholder="Product Code" className="flex-1 h-7 px-1.5 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-xs font-mono" />
                        {_isFoc && (
                          <span data-testid={`exp-gst-line-foc-chip-${idx}`} className="text-[9px] uppercase tracking-widest font-bold text-amber-500 border border-amber-500/70 rounded-sm px-1 py-0.5">FOC</span>
                        )}
                      </div>
                      {_isFoc && (ln.line_remarks || "").trim() && (
                        <div data-testid={`exp-gst-line-foc-reason-${idx}`} className="mt-1 text-[10px] italic text-amber-400 font-mono truncate" title={ln.line_remarks}>FOC — {ln.line_remarks}</div>
                      )}
                    </td>
                    <td className="px-2 py-1 font-mono text-xs text-zinc-200 whitespace-nowrap" data-testid={`exp-gst-line-style-${idx}`}>{ln.style_no || savedStyles[idx] || "—"}</td>
                    <td className="px-2 py-1"><input disabled={readOnly} data-testid={`exp-gst-line-hsn-${idx}`} value={ln.hsn_code || ""} onChange={e => patchLine(idx, { hsn_code: e.target.value })} className="w-full h-7 px-1.5 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-xs font-mono" /></td>
                    <td className="px-2 py-1"><input type="number" step="0.01" disabled={readOnly} data-testid={`exp-gst-line-qty-${idx}`} value={ln.qty ?? 0} onChange={e => patchLine(idx, { qty: e.target.value })} className="w-full h-7 px-1.5 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-xs text-right tabular-nums" /></td>
                    <td className="px-2 py-1"><input disabled={readOnly} value={ln.unit || ""} onChange={e => patchLine(idx, { unit: e.target.value })} className="w-full h-7 px-1.5 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-xs" /></td>
                    <td className="px-2 py-1"><input type="number" step="0.0001" disabled={readOnly || _isFoc} data-testid={`exp-gst-line-rate-${idx}`} value={_isFoc ? 0 : (ln.rate ?? 0)} onChange={e => patchLine(idx, { rate: e.target.value })} title={_isFoc ? "FOC line — rate pinned to 0.00" : undefined} className={"w-full h-7 px-1.5 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-xs text-right tabular-nums " + (_isFoc ? "opacity-70 cursor-not-allowed" : "")} /></td>
                    <td className="px-2 py-1"><input type="number" step="0.01" disabled={readOnly} value={ln.discount_pct ?? 0} onChange={e => patchLine(idx, { discount_pct: e.target.value })} className="w-full h-7 px-1.5 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-xs text-right tabular-nums" /></td>
                    <td className={"px-2 py-1 text-right font-mono tabular-nums " + (_isFoc ? "text-amber-400" : "text-zinc-300")}>{fmt(_isFoc ? 0 : taxable)}</td>
                    <td className={"px-2 py-1 text-right font-mono tabular-nums font-semibold " + (_isFoc ? "text-amber-400" : "")}>{fmt(_isFoc ? 0 : total)}</td>
                    {!readOnly && (
                      <td className="px-2 py-1 text-right"><button title="Remove" onClick={() => removeLine(idx)} className="text-red-400 hover:text-red-300"><TrashIcon size={12} /></button></td>
                    )}
                  </tr>
                );
              })}
            </tbody>
            {/* A1-D02-U01 · Nov 2026 · Owner §2 — TOTAL row.
                Total Qty aggregates `Σ qty` when every non-empty UOM
                matches; otherwise renders `— (mixed UOMs)` to prevent
                a misleading combined quantity (Owner D2).
                Total Value aggregates `Σ line_total` (model-aware).
                `colSpan` is a constant 6 columns before the Taxable
                column since the IGST% column has been removed. */}
            {(inv.lines || []).length > 0 && (() => {
              const _units = (inv.lines || []).map(l => (l.unit || "").trim()).filter(Boolean);
              const _distinctUnits = Array.from(new Set(_units));
              const _mixedUoM = _distinctUnits.length > 1;
              const _displayUnit = _distinctUnits[0] || "";
              const _totalQty = (inv.lines || []).reduce((a, l) => a + (Number(l.qty) || 0), 0);
              const _model = (inv.financial_model_version || "additive_v1").trim() || "additive_v1";
              const _isEciAligned = _model === "eci_aligned_v2" || _model === "eci_aligned_v3";
              const _lineTotalValue = (inv.lines || []).reduce((a, l) => {
                const q = Number(l.qty) || 0, r = Number(l.rate) || 0;
                const tx = q * r * (1 - (Number(l.discount_pct) || 0) / 100);
                const ig = (inv.export_type === "IGST") ? tx * (Number(l.gst_rate) || 0) / 100 : 0;
                return a + (_isEciAligned ? tx : (tx + ig));
              }, 0);
              return (
                <tfoot data-testid="exp-gst-lines-total-row">
                  <tr className="border-t-2 border-[var(--border)] bg-[var(--bg-2)]/30 font-mono">
                    <td className="px-2 py-2 text-right text-[10px] uppercase tracking-widest text-zinc-400" colSpan={4}>TOTAL</td>
                    <td className="px-2 py-2 text-right font-semibold tabular-nums text-zinc-200" data-testid="exp-gst-lines-total-qty">
                      {_mixedUoM
                        ? <span className="text-amber-400 text-[10px]">— (mixed UOMs)</span>
                        : <>{fmt(_totalQty)}</>}
                    </td>
                    <td className="px-2 py-2 text-left text-[10px] text-zinc-500">{_mixedUoM ? "" : _displayUnit}</td>
                    <td className="px-2 py-2" colSpan={3}></td>
                    <td className="px-2 py-2 text-right font-semibold tabular-nums text-[var(--accent)]" data-testid="exp-gst-lines-total-value">{fmt(_lineTotalValue)}</td>
                    {!readOnly && <td></td>}
                  </tr>
                </tfoot>
              );
            })()}
          </table>
        </div>

        {/* Charges + Totals */}
        <div className="grid grid-cols-2 gap-3">
          <div className="border border-[var(--border)] rounded-sm p-3 space-y-2">
            <div className="text-[11px] font-mono uppercase tracking-widest text-zinc-400">Extra Charges ({ccy})</div>
            <NumRow label="Freight"    disabled={readOnly} value={inv.charges?.freight}   onChange={v => setInv(prev => ({ ...prev, charges: { ...prev.charges, freight: v } }))} testid="exp-gst-charge-freight" />
            <NumRow label="Insurance"  disabled={readOnly} value={inv.charges?.insurance} onChange={v => setInv(prev => ({ ...prev, charges: { ...prev.charges, insurance: v } }))} testid="exp-gst-charge-insurance" />
            <NumRow label="Packing"    disabled={readOnly} value={inv.charges?.packing}   onChange={v => setInv(prev => ({ ...prev, charges: { ...prev.charges, packing: v } }))} testid="exp-gst-charge-packing" />
            <NumRow label="Other"      disabled={readOnly} value={inv.charges?.other}     onChange={v => setInv(prev => ({ ...prev, charges: { ...prev.charges, other: v } }))} testid="exp-gst-charge-other" />
            <div className="pt-2 border-t border-[var(--border)]">
              <label className="text-[11px] uppercase tracking-widest text-zinc-500">Notes</label>
              <textarea rows={2} disabled={readOnly} value={inv.notes || ""} onChange={e => setInv(prev => ({ ...prev, notes: e.target.value }))} className="w-full mt-1 px-2 py-1.5 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-xs" />
            </div>
            <div className="pt-2 border-t border-[var(--border)]">
              <label className="text-[11px] uppercase tracking-widest text-zinc-500">Export Declaration</label>
              <textarea rows={4} disabled={readOnly} data-testid="exp-gst-declaration"
                value={inv.declaration || ""}
                onChange={e => setInv(prev => ({ ...prev, declaration: e.target.value }))}
                placeholder="Auto-generated from GST treatment — customise if needed"
                className="w-full mt-1 px-2 py-1.5 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-xs" />
            </div>
          </div>
          <div className="border border-[var(--border)] rounded-sm p-3 space-y-1.5">
            <div className="text-[11px] font-mono uppercase tracking-widest text-zinc-400 mb-1">Totals · Dual Currency</div>
            {/* A1-D02-U01 · Nov 2026 · Owner D1 (R1) — Entry/View use
                the new 8-row particulars for eci_aligned_v2 and
                eci_aligned_v3 (A1-D02-U02). Historical additive_v1
                keeps the pre-U01 TotRow list verbatim so historical
                invoices and the specimen `EXP-26-27/E/00201` display
                identical numbers to before A1-D02-U01. The approved
                Browser Print 8-row layout is NOT changed by U01; U02
                only switches the final-row label + value under v3. */}
            {(() => {
              const _model = (inv.financial_model_version || "additive_v1").trim() || "additive_v1";
              const _isV2 = _model === "eci_aligned_v2";
              const _isV3 = _model === "eci_aligned_v3";
              const _isEciAligned = _isV2 || _isV3;
              if (!_isEciAligned) {
                return (
                  <>
                    <TotRow label={`Taxable (${ccy})`}     value={totals.taxable_value} />
                    {withIgst && <TotRow label="IGST" value={totals.total_igst} />}
                    <TotRow label="Freight"       value={totals.freight} />
                    <TotRow label="Insurance"     value={totals.insurance} />
                    <TotRow label="Packing"       value={totals.packing} />
                    <TotRow label="Other"         value={totals.other} />
                    <div className="border-t border-[var(--border)] pt-1.5 flex items-center justify-between font-mono">
                      <span className="text-[11px] uppercase tracking-widest text-zinc-300">Grand Total ({ccy})</span>
                      <span className="text-lg font-bold text-[var(--accent)]" data-testid="exp-gst-grand-total-fc">{ccy} {fmt(totals.grand_total_fc)}</span>
                    </div>
                    <div className="flex items-center justify-between font-mono text-zinc-400">
                      <span className="text-[10px] uppercase tracking-widest">INR @ {fmt(inv.fx_rate_to_inr)}</span>
                      <span className="text-sm" data-testid="exp-gst-grand-total-inr">₹ {fmt(totals.grand_total_inr)}</span>
                    </div>
                    <div className="pt-2 border-t border-[var(--border)] text-[10px] text-zinc-500">
                      <div><b className="text-zinc-400">FOB Value:</b> {ccy} {fmt(totals.fob_value)}</div>
                      <div><b className="text-zinc-400">Freight + Insurance:</b> {ccy} {fmt((Number(totals.freight)||0)+(Number(totals.insurance)||0))}</div>
                    </div>
                  </>
                );
              }
              const u = totals._u01;
              const fxPresent = u.fx_present;
              const _inr = (usd) => fxPresent ? fmt(usd * Number(inv.fx_rate_to_inr || 0)) : "—";
              return (
                <div data-testid="exp-gst-totals-u01">
                  <table className="w-full text-[11px] font-mono">
                    <thead className="text-[10px] uppercase tracking-widest text-zinc-500">
                      <tr>
                        <th className="text-left py-1">Particulars</th>
                        <th className="text-right py-1">USD</th>
                        <th className="text-right py-1">INR</th>
                      </tr>
                    </thead>
                    <tbody className="text-zinc-300">
                      <DualTotRow label="Line Total Value" usd={fmt(u.line_total_value)} inr={_inr(u.line_total_value)}
                                  testid="u01-line-total-value" />
                      <DualTotRow label="Freight"          usd={fmt(totals.freight)}      inr={_inr(totals.freight)}
                                  testid="u01-freight" />
                      <DualTotRow label="Insurance"        usd={fmt(totals.insurance)}    inr={_inr(totals.insurance)}
                                  testid="u01-insurance" />
                      <DualTotRow label="Discount"         usd={fmt(u.summary_discount)}  inr={_inr(u.summary_discount)}
                                  testid="u01-discount" />
                      <DualTotRow label="Total Taxable Value" usd={fmt(u.total_taxable_value)} inr={_inr(u.total_taxable_value)}
                                  bold testid="u01-total-taxable" />
                      <tr data-testid="u01-igst-rate-row">
                        <td className="py-1 text-zinc-400 uppercase tracking-widest text-[10px]">IGST Rate</td>
                        <td className="py-1 text-right tabular-nums" colSpan={2}>
                          {u.igst_rate_mixed
                            ? <span className="text-amber-400">— (mixed)</span>
                            : (u.igst_rate_pct_header != null ? `${fmt(u.igst_rate_pct_header)}%` : "0%")}
                        </td>
                      </tr>
                      <DualTotRow label="IGST Value" usd={fmt(u.igst_value_usd)} inr={_inr(u.igst_value_usd)}
                                  testid="u01-igst-value" />
                      <tr className="border-t border-[var(--border)] font-semibold" data-testid="u01-grand-total-row">
                        <td className="py-1.5 text-zinc-200 uppercase tracking-widest text-[10px]">
                          {u.grand_total_includes_igst ? "Grand Total (including IGST)" : "Grand Total (excl. IGST)"}
                        </td>
                        <td className="py-1.5 text-right tabular-nums text-[var(--accent)]" data-testid="exp-gst-grand-total-fc">{fmt(u.grand_total_usd_ex_igst)}</td>
                        <td className="py-1.5 text-right tabular-nums text-zinc-200" data-testid="exp-gst-grand-total-inr">{_inr(u.grand_total_usd_ex_igst)}</td>
                      </tr>
                    </tbody>
                  </table>
                  {!fxPresent && (
                    <div className="pt-2 text-[10px] text-amber-400" data-testid="u01-fx-missing-note">
                      Exchange Rate (USD → INR) is missing on this draft — INR amounts cannot be computed. Enter a valid FX before Save.
                    </div>
                  )}
                  <div className="pt-2 border-t border-[var(--border)] text-[10px] text-zinc-500">
                    FX used: {fxPresent ? `₹ ${fmt(inv.fx_rate_to_inr)} / ${ccy}` : "—"}
                  </div>
                </div>
              );
            })()}
          </div>
        </div>

        {/* Shipping Bill (read-only display of what stamp will fill) */}
        {(inv.shipping_bill_no || readOnly) && (
          <div className="border border-emerald-500/30 rounded-sm p-3 bg-emerald-500/5 grid grid-cols-3 gap-3" data-testid="exp-gst-sb-block">
            <Field label="Shipping Bill No"><input disabled value={inv.shipping_bill_no || "—"} className="w-full h-9 px-2 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-sm font-mono" /></Field>
            <Field label="Shipping Bill Date"><input disabled value={inv.shipping_bill_date || "—"} className="w-full h-9 px-2 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-sm" /></Field>
            <Field label="Port Code"><input disabled value={inv.port_code || "—"} className="w-full h-9 px-2 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-sm font-mono" /></Field>
          </div>
        )}

        {(inv.amendments || []).length > 0 && (
          <div className="border border-amber-500/30 rounded-sm p-3 bg-amber-500/5">
            <div className="text-[11px] font-mono uppercase tracking-widest text-amber-300 mb-2 flex items-center gap-2"><WarningIcon size={12} weight="bold" /> Amendment history</div>
            {inv.amendments.map((a, i) => (
              <div key={i} className="text-[11px] text-zinc-300 border-b border-amber-500/10 last:border-0 py-1">
                <span className="text-amber-300 font-mono">{a.at?.slice(0, 19).replace("T", " ")}</span>
                {" · "}<span className="text-zinc-400">{a.by}</span>{" · "}{a.reason}
              </div>
            ))}
          </div>
        )}

        {inv.status === "Cancelled" && inv.cancelled && (
          <div className="border border-red-500/40 rounded-sm p-3 bg-red-500/5 text-[11px] text-red-200">
            <b>Cancelled</b> · {inv.cancelled.at?.slice(0, 19).replace("T", " ")} by {inv.cancelled.by} · {inv.cancelled.reason}
          </div>
        )}

        <DialogFooter className="gap-2">
          <button data-testid="exp-gst-cancel-editor" onClick={onClose} className="h-9 px-4 border border-[var(--border)] hover:bg-[var(--bg-2)] text-xs font-mono uppercase tracking-widest rounded-sm">Close</button>
          {initial?.id && <DocDownloadButtons type="EGST" id={initial.id} testPrefix="exp-gst-view" />}
          {/* DP-EGSTI-LIFECYCLE-UI-UNIFORMITY-01 · Jun 2026 — View-popup
              Issue. Surfaces for an eligible Authorised Draft even in
              read-only View mode (the row Edit lock forces readOnly, so
              the edit-mode Issue button below never renders for an
              Authorised doc — hence no duplication). Text-labelled,
              consistent with other document View dialogs. Same eligibility
              and same canonical /issue endpoint as the row action. */}
          {issueEligible && (
            <button
              data-testid="exp-gst-view-issue"
              disabled={busy}
              onClick={issueAuthorised}
              className="h-9 px-4 bg-emerald-500 hover:bg-emerald-600 text-white text-xs font-mono uppercase tracking-widest rounded-sm disabled:opacity-40 flex items-center gap-1.5"
              title="Issue Export GST Invoice — assign final number & post (Authorise ≠ Issue)"
            >
              <CheckCircleIcon size={12} weight="bold" /> Issue
            </button>
          )}
          {!readOnly && !isAmendable && (
            <>
              <button data-testid="exp-gst-save-draft" disabled={busy} onClick={saveDraft} className="h-9 px-4 border border-[var(--accent)] text-[var(--accent)] hover:bg-[var(--accent)]/10 text-xs font-mono uppercase tracking-widest rounded-sm disabled:opacity-40">Save as Draft</button>
              {/* EGSTI-MA-H01 · Phase 2 · Mar 2026 — Submit for Approval.
                  Phase 1B.1 — ALWAYS rendered on New/Draft/Rejected,
                  DISABLED until buyer+line present. Save ≠ Submit — a
                  separate explicit action is required to enter Pending
                  Authorisation. */}
              {showSubmitForApproval && (
                <button
                  data-testid="exp-gst-submit-for-approval"
                  disabled={busy || !enableSubmitForApproval}
                  onClick={submitForApproval}
                  className="h-9 px-4 bg-amber-500 hover:bg-amber-600 text-white text-xs font-mono uppercase tracking-widest rounded-sm disabled:opacity-40 flex items-center gap-1.5"
                  title={!enableSubmitForApproval ? submitDisabledReason : (isRejected ? "Resubmit corrected Draft for Management Authorisation" : "Submit Draft for Management Authorisation")}
                >
                  {isRejected ? "Resubmit for Authorisation" : "Submit for Authorisation"}
                </button>
              )}
              {isAuthorised && (
              <button
                data-testid="exp-gst-issue"
                disabled={busy || !canIssue || issueBlockedByMA}
                onClick={issue}
                className="h-9 px-4 bg-emerald-500 hover:bg-emerald-600 text-white text-xs font-mono uppercase tracking-widest rounded-sm disabled:opacity-40 flex items-center gap-1.5"
                title={issueBlockedByMA ? "Requires Management Authorisation before Issue" : "Issue invoice"}
              >
                <CheckCircleIcon size={12} weight="bold" /> Issue Invoice
              </button>
              )}
            </>
          )}
          {isAmendable && (
            <button data-testid="exp-gst-amend-save" disabled={busy} onClick={amend} className="h-9 px-4 bg-amber-500 hover:bg-amber-600 text-white text-xs font-mono uppercase tracking-widest rounded-sm disabled:opacity-40 flex items-center gap-1.5"><PencilLineIcon size={12} weight="bold" /> Save Changes (Amend)</button>
          )}
          {isSbStamped && (
            <span data-testid="exp-gst-locked-by-sb" className="inline-flex items-center px-2 py-1 rounded-sm border border-red-500/50 text-red-300 bg-red-500/10 text-[10px] font-mono uppercase tracking-widest">Locked · Shipping Bill Stamped</span>
          )}
          {initial?.invoice_no && (
            <button data-testid="exp-gst-editor-print" disabled={busy} onClick={async () => {
              const res = await api.get(`/export-gst-invoices/${initial.id}/print.html`, { responseType: "text" });
              const blob = new Blob([res.data], { type: "text/html;charset=utf-8" });
              const url  = URL.createObjectURL(blob);
              window.open(url, "_blank");
              setTimeout(() => URL.revokeObjectURL(url), 60000);
            }} className="h-9 px-4 border border-[var(--border)] text-emerald-400 hover:bg-[var(--bg-2)] text-xs font-mono uppercase tracking-widest rounded-sm disabled:opacity-40 flex items-center gap-1.5"><PrinterIcon size={12} weight="bold" /> Print</button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children, testid }) {
  return (
    <div data-testid={testid}>
      <label className="text-[10px] uppercase tracking-widest text-zinc-500 block mb-1">{label}</label>
      {children}
    </div>
  );
}

function Party({ title, party }) {
  return (
    <div className="border border-[var(--border)] rounded-sm p-3 bg-[var(--bg-2)]/30">
      <div className="text-[11px] font-mono uppercase tracking-widest text-zinc-400 mb-1">{title}</div>
      <div className="text-sm text-white font-semibold">{party?.name || "—"}</div>
      <div className="text-[11px] text-zinc-400 whitespace-pre-line">{party?.address || "—"}</div>
      <div className="text-[11px] text-zinc-300 mt-1">
        {party?.gstin && <><span className="text-zinc-500">GSTIN:</span> {party.gstin}</>}
        {party?.iec && <> · <span className="text-zinc-500">IEC:</span> {party.iec}</>}
      </div>
      {party?.lut_no && (
        <div className="text-[10px] text-zinc-500 mt-1">LUT: {party.lut_no} · {party.lut_date || "—"}</div>
      )}
    </div>
  );
}

function PartyEditable({ title, party, readOnly, testId, hint, onChange, onCopyFromBill }) {
  const inputCls = "w-full h-7 px-1.5 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-xs";
  const areaCls  = "w-full min-h-[48px] px-1.5 py-1 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-xs resize-y";
  return (
    <div className="border border-[var(--border)] rounded-sm p-3 bg-[var(--bg-2)]/30 space-y-1.5" data-testid={testId}>
      <div className="flex items-center justify-between gap-2">
        <div className="text-[11px] font-mono uppercase tracking-widest text-zinc-400">{title}</div>
        {!readOnly && onCopyFromBill && (
          <button type="button" onClick={onCopyFromBill} data-testid={`${testId}-copy-from-bill`}
            className="text-[10px] font-mono uppercase tracking-widest text-[var(--accent)] hover:underline">Same as Buyer</button>
        )}
      </div>
      {readOnly ? (
        <>
          <div className="text-sm text-white font-semibold">{party?.name || "—"}</div>
          <div className="text-[11px] text-zinc-400 whitespace-pre-line">{party?.address || "—"}</div>
          <div className="text-[11px] text-zinc-300"><span className="text-zinc-500">Country:</span> {party?.country || "—"}</div>
          {party?.tax_id && <div className="text-[11px] text-zinc-300"><span className="text-zinc-500">Tax ID:</span> {party.tax_id}</div>}
        </>
      ) : (
        <>
          <input value={party?.name || ""} onChange={e => onChange({ ...party, name: e.target.value })} placeholder="Party name" className={`${inputCls} font-semibold text-white`} data-testid={`${testId}-name`} />
          <textarea value={party?.address || ""} onChange={e => onChange({ ...party, address: e.target.value })} placeholder="Address (multi-line)" className={areaCls} data-testid={`${testId}-address`} />
          <div className="grid grid-cols-2 gap-1.5">
            <input value={party?.country || ""} onChange={e => onChange({ ...party, country: e.target.value })} placeholder="Country" className={inputCls} data-testid={`${testId}-country`} />
            {"tax_id" in (party || {}) ? (
              <input value={party?.tax_id || ""} onChange={e => onChange({ ...party, tax_id: e.target.value })} placeholder="VAT / Tax ID" className={`${inputCls} font-mono`} data-testid={`${testId}-tax-id`} />
            ) : <span />}
          </div>
        </>
      )}
      {hint && <div className="text-[10px] text-zinc-500 italic mt-1">{hint}</div>}
    </div>
  );
}

function NumRow({ label, value, onChange, disabled, testid }) {
  return (
    <div className="flex items-center justify-between gap-2">
      <label className="text-[11px] uppercase tracking-widest text-zinc-500 flex-1">{label}</label>
      <input type="number" step="0.01" data-testid={testid} disabled={disabled} value={value ?? 0} onChange={e => onChange(e.target.value)} className="w-32 h-8 px-2 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-xs text-right tabular-nums" />
    </div>
  );
}

function TotRow({ label, value }) {
  return (
    <div className="flex items-center justify-between text-[11px] font-mono">
      <span className="text-zinc-500 uppercase tracking-widest">{label}</span>
      <span className="text-zinc-300 tabular-nums">{fmt(value)}</span>
    </div>
  );
}

// A1-D02-U01 · Nov 2026 — Dual-currency Entry/View particulars row.
// Shows the Owner-approved USD + INR columns for eci_aligned_v2 EGSTIs.
// Historical additive_v1 continues to use the single-column TotRow
// above to keep legacy totals section byte-for-byte identical.
function DualTotRow({ label, usd, inr, bold, testid }) {
  return (
    <tr data-testid={testid} className={bold ? "font-semibold" : ""}>
      <td className="py-1 text-zinc-400 uppercase tracking-widest text-[10px]">{label}</td>
      <td className="py-1 text-right tabular-nums">{usd}</td>
      <td className="py-1 text-right tabular-nums text-zinc-300">{inr}</td>
    </tr>
  );
}
